import API_BASE_URL from '../config/api';
const authHeaders = () => {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const generateMockPayments = () => {
  const payments = [];
  const statuses = ['completed', 'pending', 'failed', 'refunded', 'cancelled'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
  const customers = [
    { id: 1, name: 'John Smith', email: 'john@example.com' },
    { id: 2, name: 'Sarah Johnson', email: 'sarah@example.com' },
    { id: 3, name: 'Mike Wilson', email: 'mike@example.com' },
    { id: 4, name: 'Emily Davis', email: 'emily@example.com' },
    { id: 5, name: 'David Brown', email: 'david@example.com' },
  ];

  for (let i = 1; i <= 200; i++) {
    const customer = customers[Math.floor(Math.random() * customers.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const currency = currencies[Math.floor(Math.random() * currencies.length)];
    const amount = (Math.random() * 1000 + 10).toFixed(2);
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30));

    payments.push({
      id: i,
      transactionId: `TXN${String(i).padStart(6, '0')}`,
      customerId: customer.id,
      customerName: customer.name,
      customerEmail: customer.email,
      amount: parseFloat(amount),
      currency,
      status,
      description: `Payment for Order #${
        Math.floor(Math.random() * 1000) + 1000
      }`,
      createdAt: date.toISOString(),
      processedAt:
        status === 'completed'
          ? new Date(date.getTime() + Math.random() * 3600000).toISOString()
          : null,
      refundAmount:
        status === 'refunded'
          ? parseFloat((amount * Math.random() * 0.8).toFixed(2))
          : 0,
      fees: parseFloat((amount * 0.029 + 0.3).toFixed(2)),
    });
  }

  return payments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

const mockPayments = generateMockPayments();

// Get payments with filtering and pagination
export const getPayments = async (params = {}) => {
  const {
    page = 1,
    limit = 20,
    search = '',
    status = '',
    customer = '',
    sortBy = 'createdAt',
    sortOrder = 'desc',
  } = params;
  // Try real API first (Admin endpoint)
  try {
    const url = new URL(`${API_BASE_URL}/payments`);
    url.searchParams.set('page', page);
    url.searchParams.set('limit', limit);
    if (search) url.searchParams.set('search', search);
    if (status) url.searchParams.set('status', status);
    if (customer) url.searchParams.set('customer', customer);
    if (sortBy) url.searchParams.set('sortBy', sortBy);
    if (sortOrder) url.searchParams.set('sortOrder', sortOrder);

    const res = await fetch(url.toString(), { headers: { ...authHeaders() } });
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');

    const payments = json?.data || [];
    const pagination = json?.pagination || {
      page,
      limit,
      total: payments.length,
      totalPages: Math.ceil((payments.length || 0) / limit),
    };
    return { payments, pagination };
  } catch (_) {
    // fall back to mock below
  }

  let filteredPayments = [...mockPayments];

  // Apply filters
  if (search) {
    const searchLower = search.toLowerCase();
    filteredPayments = filteredPayments.filter(
      (payment) =>
        payment.transactionId.toLowerCase().includes(searchLower) ||
        payment.customerName.toLowerCase().includes(searchLower) ||
        payment.customerEmail.toLowerCase().includes(searchLower) ||
        payment.description.toLowerCase().includes(searchLower)
    );
  }

  if (status) {
    filteredPayments = filteredPayments.filter(
      (payment) => payment.status === status
    );
  }

  if (customer) {
    filteredPayments = filteredPayments.filter(
      (payment) => payment.customerId === parseInt(customer)
    );
  }

  // Apply sorting
  filteredPayments.sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    if (sortBy === 'createdAt' || sortBy === 'processedAt') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Apply pagination
  const total = filteredPayments.length;
  const totalPages = Math.ceil(total / limit);
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedPayments = filteredPayments.slice(startIndex, endIndex);

  return {
    payments: paginatedPayments,
    pagination: {
      page,
      limit,
      total,
      totalPages,
    },
  };
};

// Get payment by ID
export const getPayment = async (id) => {
  const payment = mockPayments.find((p) => p.id === parseInt(id));
  if (!payment) {
    throw new Error('Payment not found');
  }
  return payment;
};

// Get payment statistics
export const getPaymentStats = async (period = '7d') => {
  // Try real API first (Admin endpoint)
  try {
    const res = await fetch(`${API_BASE_URL}/payments/stats`, {
      headers: { ...authHeaders() },
    });
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    const d = json?.data || {};
    return {
      totalAmount: d.totalRevenue || 0,
      totalPayments: d.totalOrders || 0,
      completedPayments: d.paidOrders || 0,
      pendingPayments: d.pendingOrders || 0,
      failedPayments: d.failedOrders || 0,
      refundedPayments: d.refundedPayments || 0,
      successRate: Math.round(d.successRate || 0),
      averageAmount: d.averageAmount || 0,
      statusBreakdown: {
        completed: d.paidOrders || 0,
        pending: d.pendingOrders || 0,
        failed: d.failedOrders || 0,
        refunded: d.refundedPayments || 0,
      },
      topCustomers: [],
    };
  } catch (_) {
    // fallback to mock below
  }
  const now = new Date();
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 1;
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  const recentPayments = mockPayments.filter(
    (payment) => new Date(payment.createdAt) >= startDate
  );

  const totalAmount = recentPayments
    .filter((p) => p.status === 'completed')
    .reduce((sum, payment) => sum + payment.amount, 0);

  const completedCount = recentPayments.filter(
    (p) => p.status === 'completed'
  ).length;
  const pendingCount = recentPayments.filter(
    (p) => p.status === 'pending'
  ).length;
  const failedCount = recentPayments.filter(
    (p) => p.status === 'failed'
  ).length;
  const refundedCount = recentPayments.filter(
    (p) => p.status === 'refunded'
  ).length;

  const statusBreakdown = {
    completed: completedCount,
    pending: pendingCount,
    failed: failedCount,
    refunded: refundedCount,
  };

  const topCustomers = recentPayments
    .filter((p) => p.status === 'completed')
    .reduce((acc, payment) => {
      const existing = acc.find((c) => c.customerId === payment.customerId);
      if (existing) {
        existing.totalAmount += payment.amount;
        existing.count += 1;
      } else {
        acc.push({
          customerId: payment.customerId,
          customerName: payment.customerName,
          totalAmount: payment.amount,
          count: 1,
        });
      }
      return acc;
    }, [])
    .sort((a, b) => b.totalAmount - a.totalAmount)
    .slice(0, 5);

  return {
    totalAmount,
    totalPayments: recentPayments.length,
    completedPayments: completedCount,
    pendingPayments: pendingCount,
    failedPayments: failedCount,
    refundedPayments: refundedCount,
    successRate:
      recentPayments.length > 0
        ? Math.round((completedCount / recentPayments.length) * 100)
        : 0,
    averageAmount: completedCount > 0 ? totalAmount / completedCount : 0,
    statusBreakdown,
    topCustomers,
  };
};

// Get filter options
export const getPaymentFilters = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/payments/filters`, {
      headers: { ...authHeaders() },
    });
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    return json.data || { statuses: [], customers: [] };
  } catch (_) {
    // Fallback: provide only statuses, no static customers
    return {
      statuses: [
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
        { value: 'refunded', label: 'Refunded' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
      customers: [],
    };
  }
};

// Process refund
export const processRefund = async (paymentId, amount) => {
  const payment = mockPayments.find((p) => p.id === parseInt(paymentId));
  if (!payment) {
    throw new Error('Payment not found');
  }

  if (payment.status !== 'completed') {
    throw new Error('Only completed payments can be refunded');
  }

  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1000));

  // Update payment status
  payment.status = 'refunded';
  payment.refundAmount = amount;

  return {
    success: true,
    message: 'Refund processed successfully',
    refundId: `REF${String(paymentId).padStart(6, '0')}`,
  };
};
